/**
 * Ask for a name.
 *
 * The sibling of `ConfirmDialog`, and it exists for the same reason that one
 * does: the app should look like itself while it is asking a question.
 *
 * It replaces three `window.prompt` calls. A native prompt was the shortest
 * thing that worked and it was wrong on four counts, in rising order of how
 * much they matter: it is unstyled, it is untestable without stubbing a global,
 * it cannot say why the name it was given is not allowed, and **some browsers
 * suppress it entirely** — Firefox drops repeated prompts, and it is blocked
 * outright inside a cross-origin iframe. A save that silently does nothing is
 * a bad way to find out.
 *
 * Validation is `nameSchema`'s own bounds rather than a second opinion about
 * them: a name that this dialog accepts is one the command will accept, so the
 * refusal happens under the field rather than as a toast after the fact.
 */

import { NAME_MAX_LENGTH } from '@beerwright/commands';
import { useEffect, useId, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface NameDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description: string;
  /** What the field starts with. Selected on open, so typing replaces it. */
  readonly initialValue?: string;
  readonly label?: string;
  readonly confirmLabel?: string;
  /** Called with the trimmed name. Only fires when the name is allowed. */
  readonly onSubmit: (name: string) => void;
}

export function NameDialog({
  open,
  onOpenChange,
  title,
  description,
  initialValue = '',
  label = 'Name',
  confirmLabel = 'Save',
  onSubmit,
}: NameDialogProps) {
  const id = useId();
  const [value, setValue] = useState(initialValue);

  // Reopening with a different subject must not show the last one's name.
  useEffect(() => {
    if (open) setValue(initialValue);
  }, [open, initialValue]);

  const trimmed = value.trim();
  const tooLong = trimmed.length > NAME_MAX_LENGTH;
  const valid = trimmed.length > 0 && !tooLong;

  const submit = () => {
    if (!valid) return;
    onSubmit(trimmed);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor={id}>{label}</Label>
          <Input
            id={id}
            value={value}
            autoFocus
            onFocus={(event) => event.currentTarget.select()}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submit();
              }
            }}
          />
          {tooLong && (
            <p className="text-destructive text-xs">
              That is longer than {NAME_MAX_LENGTH} characters.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!valid} onClick={submit}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
